import React, { useMemo } from 'react'
import { ChargingCardToWallboxFormData } from '../charging-card-to-wallbox-form-data'
import { Control, UseFormReset } from 'react-hook-form'
import { WallboxModel } from '../../../../wallboxes/models/wallbox-model'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { Box, Button, CircularProgress, Stack } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ChargingCardLinkCell } from './ChargingCardLinkCell'
import { ChargingCardLinkWallboxCell } from './ChargingCardLinkWallboxCell'
import { SearchField } from '@e3dc-react/shell/Elements/InputFields'
import { ManagedModeInfoAlert } from '../ManagedModeInfoAlert'
import { useWallboxLabel } from '../../../../shared/hooks/use-wallbox-label'
import { ChargingCardModel } from '../../../api/models/charging-card-model'
import { useTypedSelector } from 'src/State/RootReducer'
import { useChargingCardsTableHeader } from '../../../hooks/useChargingCardsTableHeader'

interface ChargingCardToWallboxLinkDesktopViewProps {
  wallboxes: WallboxModel[]
  installationId: number
  control: Control<ChargingCardToWallboxFormData>
  reset: UseFormReset<ChargingCardToWallboxFormData>
  search: string
  chargingCards: ChargingCardModel[]
  dirtyOCPITokenId: string | undefined
  disabledOCPITokenIds: string[]
  isAssignTokenLoading: boolean
  onSubmit: () => void
  onCancelButtonClick: () => void
  onSearchChange: (param: string) => void
  isManagedMode: boolean
}

/**
 * Component for displaying table for charging card link to wallboxes for desktop version
 */
export const ChargingCardToWallboxLinkDesktopView: React.FC<ChargingCardToWallboxLinkDesktopViewProps> = ({
  wallboxes,
  installationId,
  control,
  chargingCards,
  reset,
  search,
  onSearchChange,
  dirtyOCPITokenId,
  disabledOCPITokenIds,
  isAssignTokenLoading,
  onSubmit,
  onCancelButtonClick,
  isManagedMode,
}) => {
  const { t } = useCustomTranslation()
  const { getWallboxLabel } = useWallboxLabel()
  const chargingCardsTableHeader = useChargingCardsTableHeader({ installationId, isManagedMode })

  const sortValues = useTypedSelector(state => state.eMobility.chargingCardsTable.sort)

  const header = useMemo<Header<ChargingCardModel>[]>(
    () => [
      {
        label: t('emobility.chargingCards.chargingCard'),
        value: 'ocpiTokenName',
        isSortable: false,
        valueRender: (_, __, item) => {
          if (!item) return <></>

          const shouldDisableCurrentRow = disabledOCPITokenIds.includes(item.ocpiTokenId)

          return <ChargingCardLinkCell card={item} shouldDisableCurrentRow={shouldDisableCurrentRow} />
        },
      },
      ...wallboxes.map(wallbox => ({
        label: getWallboxLabel({ wallboxId: wallbox.wallboxId }),
        value: 'ocpiTokenId',
        isSortable: false,
        valueRender: (_: string, __: number, item: ChargingCardModel | undefined) => {
          if (!item) return <></>

          const shouldDisableCurrentRow = disabledOCPITokenIds.includes(item.ocpiTokenId)

          return (
            <ChargingCardLinkWallboxCell
              control={control}
              ocpiTokenId={item.ocpiTokenId}
              wallboxId={wallbox.wallboxId}
              isCheckboxDisabled={shouldDisableCurrentRow}
            />
          )
        },
      })),
      {
        label: '',
        value: 'ocpiTokenId',
        valueRender: (_, __, item: ChargingCardModel | undefined) => {
          if (!item) return <></>

          const isCurrentRowDirty = item.ocpiTokenId === dirtyOCPITokenId

          return (
            <Stack direction="column" sx={{ visibility: isCurrentRowDirty ? 'visible' : 'hidden' }} gap={1}>
              <Button variant="contained" onClick={onSubmit} disabled={isAssignTokenLoading}>
                {isAssignTokenLoading ? <CircularProgress size={20} /> : t('common.save')}
              </Button>
              <Button variant="text" color="inherit" onClick={onCancelButtonClick} disabled={isAssignTokenLoading}>
                {t('common.cancel')}
              </Button>
            </Stack>
          )
        },
      },
    ],
    [t, wallboxes, control, isAssignTokenLoading, onCancelButtonClick, dirtyOCPITokenId, onSubmit, disabledOCPITokenIds, getWallboxLabel]
  )

  const sortedData = useMemo(() => {
    const headerItem = chargingCardsTableHeader.find(item => item.value === sortValues?.name)

    const sort =
      headerItem && sortValues
        ? chargingCards.sort((itemA, itemB) =>
            sortValues?.direction === 'asc'
              ? headerItem.sortFunction!(itemA[sortValues.name], itemB[sortValues.name])
              : headerItem.sortFunction!(itemB[sortValues.name], itemA[sortValues.name])
          )
        : chargingCards

    return sort
  }, [chargingCards, chargingCardsTableHeader, sortValues])

  return (
    <Box>
      <Stack marginY={2}>
        <SearchField value={search} label={t('emobility.chargingCards.search-card-text')} name="search" onChange={onSearchChange} delayInMs={300} />
      </Stack>
      <Box sx={theme => ({ backgroundColor: theme.palette.shades.shade04, padding: theme.spacing(3), borderRadius: 2 })}>
        <TableComplete
          header={header}
          data={sortedData}
          bodyElevation={0}
          footerElevation={0}
          headerElevation={0}
          disablePagination
          stickyHeader={{ tableHeight: 'calc(100vh - 500px)' }}
          stickyFirstColumn
          stickyLastColumn
          rowId={row => row.ocpiTokenId}
        />
      </Box>
      <ManagedModeInfoAlert
        isManagedMode={isManagedMode}
        reset={reset}
        installationId={installationId}
        chargingCards={chargingCards}
        wallboxes={wallboxes}
      />
    </Box>
  )
}
